import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { userApi } from 'features/home/home.api';
import { Card, EmptyState, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { PatternChip } from 'shared/components/PatternBar';
import { formatDateTime } from 'shared/utilities/format';

export function HistoryPage() {
  const { t, locale } = useI18n();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: queryKeys.results(page),
    queryFn: () => userApi.results({ page, per_page: 15 }),
  });

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="history-page">
      <PageMeta titleKey="meta.result" />
      <h1>{t('results.historyTitle')}</h1>
      {items.length === 0 ? <EmptyState title={t('results.empty')} /> : (
        <div className="history-grid">
          {items.map((item) => (
            <Card key={item.attempt_id || item.id}>
              <div className="history-card-head">
                <strong data-testid="history-profile-code">{item.profile_code}</strong>
                {item.is_latest ? <StatusChip tone="success">{t('results.latest')}</StatusChip> : null}
              </div>
              <p className="muted">{formatDateTime(item.completed_at, locale)}</p>
              <p>{item.version?.title}</p>
              <div className="chip-row">
                {(item.top_patterns || []).map((pattern) => (
                  <PatternChip key={pattern.id || pattern.short_code} pattern={pattern} locale={locale} />
                ))}
              </div>
              <Link className="btn btn-secondary" to={paths.result(item.attempt_id)}>{t('actions.viewResult')}</Link>
            </Card>
          ))}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
